import {
  ArrowRight,
  Bot,
  BriefcaseBusiness,
  Check,
  ClipboardCheck,
  FilePlus2,
  FileText,
  Gauge,
  Home,
  LoaderCircle,
  Map,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Square,
  UserRound,
  Waypoints,
  X,
} from 'lucide-react'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import type {
  AgentRunRecord,
  CapabilityDescriptor,
  JobApplication,
  JobOpportunity,
  OfferFlowState,
} from '../domain/model.js'
import { applicationStatusLabel } from '../domain/policies.js'

type Page = 'home' | 'resume' | 'applications' | 'profile'

interface SnapshotResponse {
  state: OfferFlowState
  capabilities: CapabilityDescriptor[]
}

interface ToastState {
  text: string
  tone: 'default' | 'error'
}

declare global {
  interface Window {
    __OFFERFLOW_API_BASE__?: string
  }
}

const NAV_ITEMS: readonly { page: Page; label: string; icon: typeof Home }[] = [
  { page: 'home', label: '首页', icon: Home },
  { page: 'resume', label: '简历', icon: FileText },
  { page: 'applications', label: '申请', icon: BriefcaseBusiness },
  { page: 'profile', label: '档案', icon: UserRound },
]

export function App(): ReactNode {
  const [page, setPage] = useState<Page>('home')
  const [snapshot, setSnapshot] = useState<SnapshotResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<'resume' | 'job' | 'agent' | null>(null)
  const [agentPreset, setAgentPreset] = useState<{ intent: AgentRunRecord['intent']; prompt: string } | null>(null)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const previousTitle = document.title
    const ownTitle = (): void => {
      if (document.title !== 'OfferFlow') document.title = 'OfferFlow'
    }
    ownTitle()
    const title = document.querySelector('title')
    const observer = new MutationObserver(ownTitle)
    if (title !== null) observer.observe(title, { childList: true, characterData: true, subtree: true })
    return () => {
      observer.disconnect()
      if (document.title === 'OfferFlow') document.title = previousTitle
    }
  }, [])

  const refresh = async (): Promise<void> => {
    try {
      setLoadError(null)
      setSnapshot(await api<SnapshotResponse>('GET', '/state'))
    } catch (error) {
      const message = messageOf(error)
      setLoadError(message)
      setToast({ text: message, tone: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void refresh() }, [])
  useEffect(() => {
    if (toast === null) return
    const timer = window.setTimeout(() => { setToast(null) }, 4200)
    return () => { window.clearTimeout(timer) }
  }, [toast])

  const openAgent = (intent: AgentRunRecord['intent'], prompt: string): void => {
    setAgentPreset({ intent, prompt })
    setModal('agent')
  }

  if (loading) {
    return <div className="of-loading"><div className="of-spinner" /></div>
  }

  if (snapshot === null) {
    return (
      <main className="of-loading">
        <section className="of-connect-error">
          <div className="of-brand-mark">O</div>
          <h1>OfferFlow</h1>
          <p>{loadError ?? '暂时无法连接服务。'}</p>
          <button className="of-button of-button-primary" onClick={() => { setLoading(true); void refresh() }}>
            <RefreshCw />重新连接
          </button>
        </section>
      </main>
    )
  }

  return (
    <div className="of-shell">
      <Sidebar page={page} onNavigate={setPage} />
      <main className="of-main">
        {page === 'home' && (
          <HomePage
            snapshot={snapshot}
            onNavigate={setPage}
            onResume={() => { setModal('resume') }}
            onJob={() => { setModal('job') }}
            onAgent={openAgent}
          />
        )}
        {page === 'resume' && <ResumePage state={snapshot.state} onAdd={() => { setModal('resume') }} />}
        {page === 'applications' && (
          <ApplicationsPage
            state={snapshot.state}
            onRefresh={refresh}
            onNotify={setToast}
            onAddJob={() => { setModal('job') }}
          />
        )}
        {page === 'profile' && (
          <ProfilePage state={snapshot.state} onRefresh={refresh} onNotify={setToast} />
        )}
      </main>
      <MobileNav page={page} onNavigate={setPage} />
      {modal === 'resume' && (
        <ResumeModal
          onClose={() => { setModal(null) }}
          onSaved={async () => { setModal(null); await refresh(); setPage('resume') }}
          onNotify={setToast}
        />
      )}
      {modal === 'job' && (
        <JobModal
          onClose={() => { setModal(null) }}
          onSaved={async () => { setModal(null); await refresh(); setPage('applications') }}
          onNotify={setToast}
        />
      )}
      {modal === 'agent' && agentPreset !== null && (
        <AgentModal
          initial={agentPreset}
          onClose={() => { setModal(null) }}
          onComplete={refresh}
        />
      )}
      {toast !== null && <div className="of-toast" data-tone={toast.tone}>{toast.text}</div>}
    </div>
  )
}

function Sidebar(props: { page: Page; onNavigate(page: Page): void }): ReactNode {
  return (
    <aside className="of-sidebar">
      <div className="of-brand">
        <div className="of-brand-mark">O</div>
        <div><strong>OfferFlow</strong><small>Powered by DSH</small></div>
      </div>
      <nav className="of-nav" aria-label="主导航">
        {NAV_ITEMS.map(item => {
          const Icon = item.icon
          return (
            <button key={item.page} className="of-nav-button" data-active={props.page === item.page} onClick={() => { props.onNavigate(item.page) }}>
              <Icon /><span>{item.label}</span>
            </button>
          )
        })}
      </nav>
      <div className="of-sidebar-foot">DSH runtime<br />OfferFlow bundle 0.1.0</div>
    </aside>
  )
}

function MobileNav(props: { page: Page; onNavigate(page: Page): void }): ReactNode {
  return (
    <nav className="of-mobile-bar" aria-label="移动导航">
      {NAV_ITEMS.map(item => {
        const Icon = item.icon
        return <button key={item.page} data-active={props.page === item.page} onClick={() => { props.onNavigate(item.page) }}><Icon />{item.label}</button>
      })}
    </nav>
  )
}

function HomePage(props: {
  snapshot: SnapshotResponse
  onNavigate(page: Page): void
  onResume(): void
  onJob(): void
  onAgent(intent: AgentRunRecord['intent'], prompt: string): void
}): ReactNode {
  const [command, setCommand] = useState('')
  const { state } = props.snapshot
  const readiness = profileReadiness(state)
  const candidate = state.profile.name || '你的求职工作台'
  const recent = state.applications.slice(-3).reverse()
  const submitCommand = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const prompt = command.trim()
    if (prompt === '') return
    props.onAgent('custom', prompt)
  }
  return (
    <div className="of-page">
      <header className="of-page-head">
        <div><h1>首页</h1><p>{candidate}，今天从最值得推进的一步开始。</p></div>
        <button className="of-icon-button" title="刷新" onClick={() => { window.location.reload() }}><RefreshCw /></button>
      </header>
      <section className="of-hero-strip">
        <div className="of-hero-main">
          <div className="of-kicker"><Sparkles />求职 Agent 已就绪</div>
          <h2>{state.applications.length === 0 ? '建立候选人事实，然后让 DSH 插件替你找机会。' : `${state.applications.filter(item => item.status !== 'submitted').length} 个申请正在推进`}</h2>
          <p>{state.profile.targetRoles.length > 0 ? `当前方向：${state.profile.targetRoles.join('、')}` : '先补全目标方向，后续搜索、匹配、简历和申请会共享同一份事实。'}</p>
          <form className="of-composer" onSubmit={submitCommand}>
            <textarea aria-label="告诉 OfferFlow 你现在想完成什么" value={command} onChange={event => { setCommand(event.currentTarget.value) }} placeholder="粘贴职位 JD、面试邀请，或直接描述今天想推进的事" />
            <button type="submit" title="开始" aria-label="开始" disabled={command.trim() === ''}><Send /></button>
          </form>
        </div>
        <div className="of-hero-aside">
          <div className="of-readiness">
            <div className="of-readiness-row"><span>候选人档案</span><strong>{readiness}%</strong></div>
            <div className="of-progress"><span style={{ width: `${readiness}%` }} /></div>
            <div className="of-readiness-row"><span>简历版本</span><strong>{state.resumes.length}</strong></div>
            <div className="of-readiness-row"><span>有效授权</span><strong>{activeGrantCount(state)}</strong></div>
          </div>
        </div>
      </section>

      <section className="of-section">
        <div className="of-section-head"><h2>开始</h2><span>三个主要入口</span></div>
        <div className="of-primary-grid">
          <ActionCard icon={FilePlus2} tone="blue" title="准备简历" detail="建立可复用的事实版本" onClick={props.onResume} />
          <ActionCard icon={BriefcaseBusiness} tone="amber" title="导入一个职位" detail="生成申请记录与准备材料" onClick={props.onJob} />
          <ActionCard icon={Search} title="自动找工作" detail="调用当前 DSH 搜索与浏览器能力" onClick={() => { props.onAgent('automatic_search', `根据我的候选人档案，搜索并筛选适合的职位。优先方向：${state.profile.targetRoles.join('、') || '请先从经历推断候选方向'}。把有明确来源的职位导入 OfferFlow。`) }} />
        </div>
      </section>

      <section className="of-section">
        <div className="of-secondary-grid">
          <SecondaryAction icon={Map} title="机会方向" detail="角色与关键词" onClick={() => { props.onAgent('direction', '基于候选人档案，给出 3 个可执行的机会方向、搜索关键词、匹配理由和需要补足的证据。') }} />
          <SecondaryAction icon={ClipboardCheck} title="面试准备" detail="职位与简历联动" onClick={() => { props.onAgent('prepare_application', '读取已有申请和选定简历，为最近一个优先申请生成针对性的面试准备。') }} />
          <SecondaryAction icon={Gauge} title="求职诊断" detail="证据与下一步" onClick={() => { props.onAgent('diagnosis', '分析当前职位、申请状态、准备材料和执行回执，诊断求职转化中的主要问题，并给出下一步。') }} />
        </div>
      </section>

      <section className="of-section">
        <div className="of-section-head"><h2>已接入能力</h2><span>{props.snapshot.capabilities.length} 个工具</span></div>
        <CapabilityBand capabilities={props.snapshot.capabilities} />
      </section>

      <section className="of-section">
        <div className="of-section-head"><h2>最近申请</h2><button className="of-button" onClick={() => { props.onNavigate('applications') }}>查看全部 <ArrowRight /></button></div>
        {recent.length === 0 ? <Empty icon={BriefcaseBusiness} title="还没有申请" detail="导入职位后会在这里形成可追踪的申请记录。" /> : (
          <div className="of-list">
            {recent.map(application => <ApplicationRow key={application.id} application={application} state={state} />)}
          </div>
        )}
      </section>
    </div>
  )
}

function ActionCard(props: { icon: typeof FileText; tone?: string; title: string; detail: string; onClick(): void }): ReactNode {
  const Icon = props.icon
  return <button className="of-action-card" onClick={props.onClick}><div className="of-action-icon" data-tone={props.tone}><Icon /></div><strong>{props.title}</strong><p>{props.detail}</p><ArrowRight /></button>
}

function SecondaryAction(props: { icon: typeof FileText; title: string; detail: string; onClick(): void }): ReactNode {
  const Icon = props.icon
  return <button className="of-secondary" onClick={props.onClick}><Icon /><div><strong>{props.title}</strong><span>{props.detail}</span></div></button>
}

function CapabilityBand({ capabilities }: { capabilities: readonly CapabilityDescriptor[] }): ReactNode {
  const grouped = new globalThis.Map<string, CapabilityDescriptor>()
  for (const capability of capabilities.filter(item => item.source !== 'offerflow' && item.kind !== 'general')) {
    const key = `${capability.kind}:${capability.provider}`
    if (!grouped.has(key)) grouped.set(key, capability)
  }
  const visible = [...grouped.values()].slice(0, 8)
  if (visible.length === 0) return <Empty icon={Waypoints} title="等待生态插件" detail="已安装的 DSH 工具会自动出现在这里。" />
  return <div className="of-capability-band">{visible.map(item => <div className="of-capability" key={`${item.kind}:${item.provider}`}><div className="of-capability-top"><strong title={item.provider}>{capabilityName(item.provider)}</strong><span className="of-dot" /></div><small>{capabilityKindName(item.kind)} · {item.source === 'dsh-plugin' ? 'DSH 插件' : 'DSH 工具'}</small></div>)}</div>
}

function ResumePage(props: { state: OfferFlowState; onAdd(): void }): ReactNode {
  return (
    <div className="of-page">
      <header className="of-page-head"><div><h1>简历</h1><p>所有岗位准备都从可验证的基础版本派生。</p></div><button className="of-button of-button-primary" onClick={props.onAdd}><Plus />新建版本</button></header>
      {props.state.resumes.length === 0 ? <Empty icon={FileText} title="还没有简历版本" detail="先建立一个基础版本。" action={<button className="of-button of-button-primary" onClick={props.onAdd}><Plus />新建版本</button>} /> : (
        <div className="of-resume-grid">
          {props.state.resumes.map(resume => (
            <article className="of-resume-card" key={resume.id}>
              <div className="of-resume-card-head"><div><h3>{resume.name}</h3><span className="of-list-meta">更新于 {formatDate(resume.updatedAt)}</span></div><span className="of-source">{resume.source}</span></div>
              <p>{resume.summary}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}

function ApplicationsPage(props: { state: OfferFlowState; onRefresh(): Promise<void>; onNotify(value: ToastState): void; onAddJob(): void }): ReactNode {
  const [selectedId, setSelectedId] = useState(props.state.applications.at(-1)?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [executingId, setExecutingId] = useState<string | null>(null)
  const selected = props.state.applications.find(item => item.id === selectedId) ?? props.state.applications.at(-1)
  const opportunity = selected === undefined ? undefined : findOpportunity(props.state, selected.opportunityId)
  const activeGrant = selected === undefined ? undefined : [...props.state.grants].reverse().find(grant =>
    grant.applicationIds.includes(selected.id)
    && selected.selectedResumeId !== null
    && grant.resumeIds.includes(selected.selectedResumeId)
    && grant.actions.includes('upload_resume')
    && grant.actions.includes('submit_application')
    && grant.channels.includes('browser')
    && grant.revokedAt === null
    && Date.parse(grant.validUntil) > Date.now())

  useEffect(() => {
    if (selectedId === '' && props.state.applications.length > 0) setSelectedId(props.state.applications.at(-1)!.id)
  }, [props.state.applications, selectedId])

  const act = async (operation: () => Promise<unknown>, success: string): Promise<void> => {
    setBusy(true)
    try {
      await operation()
      await props.onRefresh()
      props.onNotify({ text: success, tone: 'default' })
    } catch (error) {
      await props.onRefresh()
      props.onNotify({ text: messageOf(error), tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const prepare = async (): Promise<void> => {
    if (selected === undefined) return
    const resumeId = selected.selectedResumeId ?? props.state.resumes.at(-1)?.id
    if (resumeId === undefined) { props.onNotify({ text: '请先创建简历版本', tone: 'error' }); return }
    await act(() => api('POST', '/applications/prepare', { applicationId: selected.id, resumeId }), '申请材料已准备')
  }
  const grant = async (): Promise<void> => {
    if (selected?.selectedResumeId === null || selected === undefined) { props.onNotify({ text: '请先完成申请准备', tone: 'error' }); return }
    const validUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    await act(() => api('POST', '/grants', {
      applicationIds: [selected.id], resumeIds: [selected.selectedResumeId],
      actions: ['upload_resume', 'submit_application'], channels: ['browser'], validUntil, volumeLimit: 1,
    }), '已授予 24 小时自动申请权限')
  }
  const revoke = async (): Promise<void> => {
    if (activeGrant === undefined) return
    await act(() => api('POST', '/grants/revoke', { grantId: activeGrant.id }), '自动申请权限已撤销')
  }
  const execute = async (): Promise<void> => {
    if (selected === undefined) return
    setExecutingId(selected.id)
    await act(async () => {
      const result = await api<JobApplication>('POST', '/applications/execute', { applicationId: selected.id, channel: 'browser' })
      if (result.status === 'needs_attention') throw new Error(result.lastError ?? '执行需要人工处理')
      if (result.status === 'paused') throw new Error('执行已暂停')
      return result
    }, '申请已执行并记录回执')
    setExecutingId(null)
  }
  const pause = async (): Promise<void> => {
    if (executingId === null) return
    try {
      await api('POST', '/applications/pause', { applicationId: executingId })
      await props.onRefresh()
      props.onNotify({ text: '自动执行已暂停', tone: 'default' })
    } catch (error) {
      props.onNotify({ text: messageOf(error), tone: 'error' })
    }
  }

  return (
    <div className="of-page">
      <header className="of-page-head"><div><h1>申请</h1><p>准备、授权、执行、回执和后续跟进在同一条记录里。</p></div><button className="of-button of-button-primary" onClick={props.onAddJob}><Plus />导入职位</button></header>
      {props.state.applications.length === 0 ? <Empty icon={BriefcaseBusiness} title="还没有申请" detail="导入一个目标职位。" action={<button className="of-button of-button-primary" onClick={props.onAddJob}><Plus />导入职位</button>} /> : (
        <div className="of-split">
          <div className="of-list">
            {[...props.state.applications].reverse().map(application => (
              <button className="of-list-row" style={{ width: '100%', borderInline: 0, borderTop: 0, textAlign: 'left' }} data-compact="true" data-selected={selected?.id === application.id} key={application.id} onClick={() => { setSelectedId(application.id) }}>
                <ApplicationRow application={application} state={props.state} compact />
              </button>
            ))}
          </div>
          {selected !== undefined && opportunity !== undefined && (
            <section className="of-detail">
              <div className="of-detail-head"><div><h2>{opportunity.role}</h2><p>{opportunity.company}{opportunity.location === '' ? '' : ` · ${opportunity.location}`}</p></div><Status status={selected.status} /></div>
              <div className="of-actions" style={{ paddingTop: 18 }}>
                <button className="of-button" disabled={busy} onClick={() => { void prepare() }}><Sparkles />准备材料</button>
                {activeGrant === undefined
                  ? <button className="of-button" disabled={busy || selected.preparation === null} onClick={() => { void grant() }}><ShieldCheck />授权自动申请</button>
                  : <button className="of-button of-button-danger" disabled={busy} onClick={() => { void revoke() }}><X />撤销授权</button>}
                {executingId === selected.id
                  ? <button className="of-button of-button-danger" onClick={() => { void pause() }}><Square />停止执行</button>
                  : <button className="of-button of-button-primary" disabled={busy || selected.status !== 'ready'} onClick={() => { void execute() }}>{busy ? <LoaderCircle /> : <Send />}开始执行</button>}
              </div>
              {selected.lastError !== null && <div className="of-detail-section"><h3>需处理</h3><p>{selected.lastError}</p></div>}
              <div className="of-detail-section"><h3>自动申请授权</h3>{activeGrant === undefined ? <p>当前没有覆盖此申请的有效授权。</p> : <p>浏览器渠道 · 上传选定简历并提交申请 · 截止 {formatDateTime(activeGrant.validUntil)} · 剩余 {Math.max(0, activeGrant.volumeLimit - props.state.receipts.filter(receipt => receipt.grantId === activeGrant.id && receipt.outcome === 'completed').length)} 次</p>}</div>
              <div className="of-detail-section"><h3>职位描述</h3><p>{opportunity.description}</p></div>
              <div className="of-detail-section"><h3>匹配证据</h3>{opportunity.fitEvidence.length === 0 ? <p>尚未生成</p> : <ul>{opportunity.fitEvidence.map(item => <li key={item}>{item}</li>)}</ul>}</div>
              <div className="of-detail-section"><h3>申请准备</h3>{selected.preparation === null ? <p>尚未准备</p> : <><p>{selected.preparation.fitAnalysis}</p><ul>{selected.preparation.tailoredResumeNotes.map(item => <li key={item}>{item}</li>)}</ul></>}</div>
              <div className="of-detail-section"><h3>执行回执</h3>{props.state.receipts.filter(item => item.applicationId === selected.id).length === 0 ? <p>暂无回执</p> : <ul>{props.state.receipts.filter(item => item.applicationId === selected.id).map(receipt => <li key={receipt.id}>{receipt.provider} · {receipt.detail}</li>)}</ul>}</div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function ApplicationRow(props: { application: JobApplication; state: OfferFlowState; compact?: boolean }): ReactNode {
  const opportunity = findOpportunity(props.state, props.application.opportunityId)
  const content = <><div className="of-list-main"><strong>{opportunity?.role ?? '未知职位'}</strong><span>{opportunity?.company ?? '未知公司'}</span></div>{!props.compact && <div className="of-list-meta">{opportunity?.location || '地点未填写'}</div>}<Status status={props.application.status} />{!props.compact && <ArrowRight style={{ width: 16 }} />}</>
  return props.compact ? content : <div className="of-list-row">{content}</div>
}

function Status({ status }: { status: JobApplication['status'] }): ReactNode {
  return <span className="of-status" data-status={status}>{applicationStatusLabel(status)}</span>
}

function ProfilePage(props: { state: OfferFlowState; onRefresh(): Promise<void>; onNotify(value: ToastState): void }): ReactNode {
  const profile = props.state.profile
  const [busy, setBusy] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    const data = new FormData(event.currentTarget)
    try {
      await api('POST', '/profile', {
        name: data.get('name'), headline: data.get('headline'), location: data.get('location'),
        yearsOfExperience: nullableNumber(data.get('yearsOfExperience')),
        skills: csv(data.get('skills')), targetRoles: csv(data.get('targetRoles')),
        preferredLocations: csv(data.get('preferredLocations')),
        salaryExpectation: data.get('salaryExpectation'), constraints: csv(data.get('constraints')),
      })
      await props.onRefresh()
      props.onNotify({ text: '候选人档案已更新', tone: 'default' })
    } catch (error) {
      props.onNotify({ text: messageOf(error), tone: 'error' })
    } finally { setBusy(false) }
  }
  return (
    <div className="of-page">
      <header className="of-page-head"><div><h1>档案</h1><p>供简历、匹配和 Agent 工作流共同使用的候选人事实。</p></div></header>
      <form className="of-profile-panel of-form" onSubmit={event => { void submit(event) }}>
        <div className="of-form-grid">
          <Field label="姓名"><input className="of-input" name="name" defaultValue={profile.name} /></Field>
          <Field label="当前定位"><input className="of-input" name="headline" defaultValue={profile.headline} /></Field>
          <Field label="所在城市"><input className="of-input" name="location" defaultValue={profile.location} /></Field>
          <Field label="工作年限"><input className="of-input" name="yearsOfExperience" type="number" min="0" defaultValue={profile.yearsOfExperience ?? ''} /></Field>
          <Field label="目标岗位"><input className="of-input" name="targetRoles" defaultValue={profile.targetRoles.join('，')} /></Field>
          <Field label="意向地点"><input className="of-input" name="preferredLocations" defaultValue={profile.preferredLocations.join('，')} /></Field>
          <Field label="核心技能" full><textarea className="of-textarea" name="skills" defaultValue={profile.skills.join('，')} /></Field>
          <Field label="薪资期望"><input className="of-input" name="salaryExpectation" defaultValue={profile.salaryExpectation} /></Field>
          <Field label="明确约束"><input className="of-input" name="constraints" defaultValue={profile.constraints.join('，')} /></Field>
        </div>
        <div className="of-actions"><button className="of-button of-button-primary" disabled={busy} type="submit">{busy ? <LoaderCircle /> : <Check />}保存档案</button></div>
      </form>
    </div>
  )
}

function ResumeModal(props: { onClose(): void; onSaved(): Promise<void>; onNotify(value: ToastState): void }): ReactNode {
  const [busy, setBusy] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault(); setBusy(true)
    const data = new FormData(event.currentTarget)
    try { await api('POST', '/resumes', { name: data.get('name'), summary: data.get('summary') }); await props.onSaved() }
    catch (error) { props.onNotify({ text: messageOf(error), tone: 'error' }); setBusy(false) }
  }
  return <Modal title="新建简历版本" onClose={props.onClose}><form onSubmit={event => { void submit(event) }}><div className="of-modal-body of-form"><Field label="版本名称"><input className="of-input" name="name" autoFocus /></Field><Field label="事实摘要"><textarea className="of-textarea" name="summary" rows={7} /></Field></div><ModalFoot busy={busy} onClose={props.onClose} label="创建版本" /></form></Modal>
}

function JobModal(props: { onClose(): void; onSaved(): Promise<void>; onNotify(value: ToastState): void }): ReactNode {
  const [busy, setBusy] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault(); setBusy(true)
    const data = new FormData(event.currentTarget)
    try {
      await api('POST', '/opportunities', {
        company: data.get('company'), role: data.get('role'), location: data.get('location'),
        sourceUrl: data.get('sourceUrl'), sourceProvider: 'manual', compensation: data.get('compensation'),
        description: data.get('description'),
      })
      await props.onSaved()
    } catch (error) { props.onNotify({ text: messageOf(error), tone: 'error' }); setBusy(false) }
  }
  return <Modal title="导入职位" onClose={props.onClose}><form onSubmit={event => { void submit(event) }}><div className="of-modal-body of-form"><div className="of-form-grid"><Field label="公司"><input className="of-input" name="company" autoFocus /></Field><Field label="职位"><input className="of-input" name="role" /></Field><Field label="地点"><input className="of-input" name="location" /></Field><Field label="薪资"><input className="of-input" name="compensation" /></Field><Field label="职位链接" full><input className="of-input" name="sourceUrl" type="url" /></Field><Field label="职位描述" full><textarea className="of-textarea" name="description" rows={8} /></Field></div></div><ModalFoot busy={busy} onClose={props.onClose} label="导入职位" /></form></Modal>
}

function AgentModal(props: { initial: { intent: AgentRunRecord['intent']; prompt: string }; onClose(): void; onComplete(): Promise<void> }): ReactNode {
  const [prompt, setPrompt] = useState(props.initial.prompt)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<AgentRunRecord | null>(null)
  const run = async (): Promise<void> => {
    setBusy(true); setResult(null)
    try { const record = await api<AgentRunRecord>('POST', '/agent/run', { intent: props.initial.intent, prompt }); setResult(record); await props.onComplete() }
    catch (error) { setResult({ id: '', intent: props.initial.intent, prompt, sessionId: null, status: 'failed', output: '', tools: [], error: messageOf(error), startedAt: '', completedAt: '' }) }
    finally { setBusy(false) }
  }
  return <Modal title="OfferFlow Agent" onClose={props.onClose}><div className="of-modal-body of-form"><Field label="工作目标"><textarea className="of-textarea" rows={6} value={prompt} onChange={event => { setPrompt(event.target.value) }} /></Field>{busy && <div className="of-output"><LoaderCircle style={{ width: 17, verticalAlign: 'middle', marginRight: 8 }} />DSH Agent 正在调用已安装能力...</div>}{result !== null && <div className="of-output">{result.error ?? (result.output || '工作流已完成。')}</div>}</div><div className="of-modal-foot"><button className="of-button" onClick={props.onClose}>关闭</button><button className="of-button of-button-primary" disabled={busy || prompt.trim() === ''} onClick={() => { void run() }}>{busy ? <LoaderCircle /> : <Bot />}运行</button></div></Modal>
}

function Modal(props: { title: string; children: ReactNode; onClose(): void }): ReactNode {
  return <div className="of-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) props.onClose() }}><section className="of-modal" role="dialog" aria-modal="true" aria-label={props.title}><header className="of-modal-head"><h2>{props.title}</h2><button className="of-icon-button" title="关闭" onClick={props.onClose}><X /></button></header>{props.children}</section></div>
}

function ModalFoot(props: { busy: boolean; onClose(): void; label: string }): ReactNode {
  return <div className="of-modal-foot"><button className="of-button" type="button" onClick={props.onClose}>取消</button><button className="of-button of-button-primary" disabled={props.busy} type="submit">{props.busy ? <LoaderCircle /> : <Check />}{props.label}</button></div>
}

function Field(props: { label: string; full?: boolean; children: ReactNode }): ReactNode {
  return <div className={`of-field${props.full === true ? ' of-field-full' : ''}`}><label>{props.label}</label>{props.children}</div>
}

function Empty(props: { icon: typeof FileText; title: string; detail: string; action?: ReactNode }): ReactNode {
  const Icon = props.icon
  return <div className="of-empty"><div><Icon /><strong>{props.title}</strong><div>{props.detail}</div>{props.action !== undefined && <div style={{ marginTop: 16 }}>{props.action}</div>}</div></div>
}

function findOpportunity(state: OfferFlowState, id: string): JobOpportunity | undefined {
  return state.opportunities.find(item => item.id === id)
}

function profileReadiness(state: OfferFlowState): number {
  const fields = [state.profile.name, state.profile.headline, state.profile.location, state.profile.skills.length, state.profile.targetRoles.length, state.resumes.length]
  return Math.round(fields.filter(Boolean).length / fields.length * 100)
}

function activeGrantCount(state: OfferFlowState): number {
  const now = Date.now()
  return state.grants.filter(grant => grant.revokedAt === null && Date.parse(grant.validUntil) > now).length
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric' }).format(new Date(value))
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}

function capabilityName(provider: string): string {
  return provider
    .replace(/^@deepseek-ai\/dsh-/, '')
    .replaceAll('_', ' ')
}

function capabilityKindName(kind: CapabilityDescriptor['kind']): string {
  return {
    browser: '浏览器',
    search: '检索',
    email: '邮件',
    calendar: '日历',
    documents: '文档',
    messaging: '消息',
    general: '通用',
  }[kind]
}

function csv(value: FormDataEntryValue | null): string[] {
  return String(value ?? '').split(/[，,\n]/).map(item => item.trim()).filter(Boolean)
}

function nullableNumber(value: FormDataEntryValue | null): number | null {
  const text = String(value ?? '').trim()
  return text === '' ? null : Number(text)
}

async function api<T = unknown>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const base = window.__OFFERFLOW_API_BASE__ ?? '/offerflow/api'
  const response = await fetch(`${base}${path}`, {
    method,
    credentials: 'same-origin',
    ...(body === undefined ? {} : {
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),
  })
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    throw new Error('服务暂不可用，请稍后重新连接。')
  }
  const value = await response.json() as T & { error?: string }
  if (!response.ok) throw new Error(value.error ?? `请求失败 (${response.status})`)
  return value
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
